<template>
  <div>
    <InputFilter
      class="pt-10"
      :hideDetails="false"
      :hint="hint"
      :loading="loading"
    />

    <section class="filter-area">
      <v-container class="py-0">
        <v-row>
          <v-col cols="12" sm="10" class="mx-auto py-0">
            <v-row justify="space-between">
              <v-col cols="12" sm="4" class="primary-select pb-0">
                <v-autocomplete
                  class="pt-0"
                  color="#0088c3"
                  :items="transactionsTypes"
                  item-text="name"
                  item-value="id"
                  :label="$t('table.transaction_type')"
                  :loading="loading"
                  :disabled="loading || loadingLocators"
                  clearable
                  v-model="filterInputs.type"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" sm="4" class="success-select pb-0">
                <v-autocomplete
                  class="pt-0"
                  color="#1B5E20"
                  :items="subInventories"
                  item-text="name"
                  item-value="id"
                  :label="$t('table.sub_inventory')"
                  :loading="loading"
                  :disabled="loading || loadingLocators"
                  clearable
                  @change="setInventoryLocators"
                  v-model="filterInputs.inventory"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" sm="4" class="error-select pb-0">
                <v-autocomplete
                  class="pt-0"
                  color="#B71C1C"
                  :items="locators"
                  item-text="name"
                  item-value="id"
                  :label="$t('table.locator')"
                  :loading="loading"
                  :disabled="
                    loading || loadingLocators || !filterInputs.inventory
                  "
                  clearable
                  v-model="filterInputs.locator_id"
                ></v-autocomplete>
              </v-col>

              <v-col class="d-flex align-center pt-0" cols="12" sm="3">
                <span> {{ $t("transaction_date") }} </span>
              </v-col>

              <v-col class="d-flex align-center pt-0" cols="12" sm="4">
                <v-menu
                  ref="transactionsFromDateMenu"
                  v-model="showFromDateMenu"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="filterInputs.from"
                      :label="$t('from_date')"
                      color="primary"
                      v-on="on"
                      clearable
                      outlined
                      dense
                      hide-details
                      :disabled="loading || loadingLocators"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    no-title
                    :max="filterInputs.to || dateToIsoString(filterInputs.to)"
                    v-model="filterInputs.from"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col
                class="d-sm-flex justify-center align-center pt-0 d-none"
                cols="1"
              >
                <span class="indigo--text text--lighten-3"> - </span>
              </v-col>

              <v-col class="d-flex align-center pt-0" cols="12" sm="4">
                <v-menu
                  ref="transactionsToDateMenu"
                  v-model="showToDateMenu"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                  style="z-index: 10000 !important"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="filterInputs.to"
                      :label="$t('to_date')"
                      color="primary"
                      v-on="on"
                      clearable
                      outlined
                      dense
                      hide-details
                      :disabled="loading || loadingLocators"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    no-title
                    :min="
                      filterInputs.from && dateToIsoString(filterInputs.from)
                    "
                    :max="dateToIsoString('')"
                    v-model="filterInputs.to"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
</template>

<script>
import { IndexData } from "@/helpers/apiMethods";

export default {
  name: "SCMTransactionsFilterComponent",
  components: {
    InputFilter: () => import("@/views/accounts/components/InputFilter")
  },
  props: {
    loading: {
      type: Boolean,
      required: true
    },
    subInventories: {
      type: Array,
      required: true
    },
    transactionsTypes: {
      type: Array,
      required: true
    }
  },
  async mounted() {
    this.mappingQueryFiltersInputs = true;
    if (this.$route.query.locator_type) {
      this.filterInputs.locator_type = this.$route.query.locator_type;
      this.filterInputs.locator_type !== "none" && (await this.fetchLocators());
    }

    Object.keys(this.filterInputs).forEach(key => {
      this.filterInputs[key] = this.$route.query[key] || "";
    });
    this.mappingQueryFiltersInputs = false;
  },
  data() {
    return {
      locators: [],
      filterInputs: {
        inventory: "",
        type: "",
        from: "",
        to: "",
        locator_type: "",
        locator_id: ""
      },
      showFromDateMenu: false,
      showToDateMenu: false,
      loadingLocators: false,
      mappingQueryFiltersInputs: true,
      hint: this.$t("search_hint")
    };
  },
  computed: {
    selectedSCMInventoryInfo() {
      const selectedSCMInventory = this.filterInputs.inventory
        ? this.subInventories.find(
            inventory =>
              inventory.id.toString() === this.filterInputs.inventory.toString()
          )
        : {};
      return selectedSCMInventory || {};
    }
  },
  methods: {
    async fetchLocators() {
      try {
        const reqName =
          this.filterInputs.locator_type === "camp"
            ? "scm/camps"
            : "scm/employees";

        this.loadingLocators = true;
        const res = await IndexData({ reqName, query: { pagination: "all" } });
        this.locators = res.data.data.map(locator => ({
          ...locator,
          id: locator.id.toString()
        }));
      } catch (err) {
        console.log();
      } finally {
        this.loadingLocators = false;
      }
    },
    setInventoryLocators() {
      this.filterInputs.locator_id = "";
      if (!this.selectedSCMInventoryInfo.id) {
        this.filterInputs.locator_type = "";
        return;
      }
      if (
        !this.selectedSCMInventoryInfo.for_camps &&
        !this.selectedSCMInventoryInfo.for_employees
      ) {
        this.filterInputs.locator_type = "none";
        return;
      }

      this.filterInputs.locator_type = this.selectedSCMInventoryInfo.for_camps
        ? "camp"
        : "employee";
      this.fetchLocators();
    },
    dateToIsoString(date) {
      const dateObj = date ? new Date(date) : new Date();
      return dateObj.toISOString();
    }
  },
  watch: {
    filterInputs: {
      handler(filtersVal, oldFiltersVal) {
        if (
          this.mappingQueryFiltersInputs ||
          (oldFiltersVal.locator_id && !filtersVal.locator_id) ||
          (oldFiltersVal.locator_type && !filtersVal.locator_type)
        ) {
          return;
        }

        const queryData = Object.keys(filtersVal).reduce((acc, key) => {
          acc[key] = filtersVal[key] || "";
          return acc;
        }, {});
        this.$router
          .push({
            query: { ...this.$route.query, ...queryData, page: 1 }
          })
          .catch(() => {
            console.log();
          });
      },
      deep: true
    }
  }
};
</script>
